import { useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

/**
 * Shows the public IP other systems see for our outbound requests (useful for
 * allow-listing). System Administrator only, enforced by the backend; the card
 * renders nothing for anyone else.
 */
export function ExternalIpCard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const allowed = hasGlobalPermission(user, PERM.SYSTEM_DEV_TOOLS);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!allowed) return null;

  const lookup = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/system/external-ip");
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("settings.externalIpFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="settings-external-ip-card">
      <div className="mb-1 flex items-center gap-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t("settings.externalIp")}</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">{t("settings.externalIpSub")}</p>
      <div className="flex items-center gap-4">
        <Button data-testid="settings-external-ip-button" variant="outline" onClick={lookup} disabled={loading}>
          {t("settings.externalIpButton")}
        </Button>
        {result && (
          <span className="font-mono text-sm" data-testid="settings-external-ip-value">
            {result.ip}
          </span>
        )}
      </div>
    </Card>
  );
}
